import React, {
  Children,
  createContext,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
  useRef,
} from "react";
import Image from "next/image";
import classnames from "classnames";
import { useRouter } from "next/router";
import Markdown, { Components } from "react-markdown";
import { markdownUrlTransform } from "utils/markdownUrlTransform";

import { ButtonV2 } from "@/ui-kit";
import Gutter from "../../../ui-kit/Gutter";
import { Checkmark } from "ui-kit/icons";

import { useWebTranslation } from "../../../translations";
import { logEvent } from "../../../analytics";

import styles from "./StandardHeroV2.module.css";
import { Asset } from "components-shared/ContentfulMedia";
import { Hero_c2A } from "../../../../__generated__/graphql";
import { useResumePreviewStore } from "./useResumePreviewStore";

import { getMeetingBookingUrl } from "utils/site";
import { CalendlyPopUp } from "../ContentfulComponent/Calendly/PopUp";
import { FloatingCTA } from "../../FloatingCTA/FloatingCTA";
import { ContentfulC2A } from "../ContentfulC2A";
import { isWidgetC2aType } from "../ContentfulC2A/c2aTypes";
import { usePartnerTheme } from "../../../hooks/usePartnerTheme";
import { useMatchMedia } from "../../../hooks/useMatchMedia";

type HeroProps = {
  title: string | null;
  text?: string | null;
  disclaimer?: string | null;
  media: Asset;
  c2a?: Hero_c2A | null;
  thumbnail: Asset;
  desktopPosterImage?: Asset;
  mobilePosterImage?: Asset;
  hideButtons?: boolean | null;
  /**
   * Trust marks drawn under the CTAs — Trustpilot, Defaqto, whatever a market
   * uploads on its hero entry. Each keeps its own artwork and aspect ratio, so
   * the row only caps their height.
   */
  badges?: Asset[];
};

// Editors write the description in markdown, for emphasis and lists only.
// Anything else (headings, links, images) shows as its plain text.
const DESCRIPTION_ELEMENTS = ["p", "strong", "em", "ul", "ol", "li"];

// Each list item sits beside a glass disc: a checkmark in a bullet list, its
// number in a numbered list. Markdown gives an <li> no way to tell which list
// it is in, or where, so each list hands its items their marker.
const ListMarker = createContext<ReactNode>(null);

function DescriptionListItem({ children }: { children?: ReactNode }) {
  return (
    <li className={styles.listItem}>
      <span className={styles.marker} aria-hidden="true">
        {useContext(ListMarker)}
      </span>
      <span>{children}</span>
    </li>
  );
}

// list-style: none makes Safari drop the list semantics; role="list" keeps
// them.
const descriptionComponents: Components = {
  ul: ({ children }) => (
    <ListMarker.Provider
      value={
        <span className={styles.check}>
          <Checkmark />
        </span>
      }
    >
      <ul role="list">{children}</ul>
    </ListMarker.Provider>
  ),
  ol: ({ children, start }) => {
    let number = start ?? 1;

    return (
      <ol role="list" start={start}>
        {Children.map(children, (child) =>
          isValidElement(child) ? (
            <ListMarker.Provider
              value={<span className={styles.number}>{number++}</span>}
            >
              {child}
            </ListMarker.Provider>
          ) : (
            child
          )
        )}
      </ol>
    );
  },
  li: DescriptionListItem,
};

// The media swap in StandardHeroV2.module.css: the mobile media shows at the
// `desktop` breakpoint (1200px) and below, the desktop media everywhere else.
const MOBILE_MEDIA_QUERY = "screen and (max-width: 1200px)";
const DESKTOP_MEDIA_QUERY = `not ${MOBILE_MEDIA_QUERY}`;

// A 1x1 transparent GIF, for a <picture> source that must fetch nothing.
const EMPTY_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

// An eager image downloads whether or not CSS displays it. Where this one is
// hidden, the browser takes the empty source instead of fetching it. No script
// is involved, so the visible image still loads with the HTML.
function HeroImage({
  image,
  alt,
  hiddenAt,
  className,
  fallbackWidth,
  fallbackHeight,
}: {
  image: NonNullable<Asset> & { url: string };
  alt: string;
  hiddenAt: string;
  className: string;
  fallbackWidth: number;
  fallbackHeight: number;
}) {
  return (
    <picture>
      <source media={hiddenAt} srcSet={EMPTY_IMAGE} />
      <Image
        src={image.url}
        alt={alt}
        width={image.width ?? fallbackWidth}
        height={image.height ?? fallbackHeight}
        className={className}
        // Not `priority`: its preload link has no media condition, so it
        // would fetch both breakpoints' images all the same.
        loading="eager"
        fetchPriority="high"
      />
    </picture>
  );
}

function HeroAsset({
  asset,
  poster,
  isOnScreen,
  hiddenAt,
  fallbackWidth,
  fallbackHeight,
}: {
  asset: NonNullable<Asset>;
  poster?: Asset;
  isOnScreen: boolean;
  // The media query at which CSS hides this asset.
  hiddenAt: string;
  fallbackWidth: number;
  fallbackHeight: number;
}) {
  if (!asset.url) return null;
  const isVideo = asset.contentType?.startsWith("video/");
  if (isVideo) {
    return (
      <>
        {/* The poster is an image under the video rather than its `poster`
            attribute: a poster attribute is fetched even where CSS hides the
            video. The video paints over it from its first frame. */}
        {poster?.url && (
          <HeroImage
            image={{ ...poster, url: poster.url }}
            alt=""
            hiddenAt={hiddenAt}
            className={classnames(styles.media, styles.layer)}
            fallbackWidth={fallbackWidth}
            fallbackHeight={fallbackHeight}
          />
        )}
        {/* Both breakpoints' videos are in the markup and CSS hides one, but a
            hidden video with a source downloads in full all the same. Only the
            one on screen gets its source, once the client knows the viewport. */}
        <video
          key={asset.url}
          autoPlay
          muted
          loop
          playsInline
          controls={false}
          src={isOnScreen ? asset.url : undefined}
          preload={isOnScreen ? "auto" : "none"}
          className={classnames(styles.media, styles.layer)}
        />
      </>
    );
  }
  return (
    <HeroImage
      image={{ ...asset, url: asset.url }}
      alt={asset.title ?? asset.description ?? ""}
      hiddenAt={hiddenAt}
      className={styles.media}
      fallbackWidth={fallbackWidth}
      fallbackHeight={fallbackHeight}
    />
  );
}

export function StandardHeroV2({
  title,
  disclaimer,
  text,
  media,
  thumbnail,
  desktopPosterImage,
  mobilePosterImage,
  hideButtons,
  c2a,
  badges = [],
}: HeroProps): ReactElement {
  const { t } = useWebTranslation();
  const router = useRouter();
  const signupState = useResumePreviewStore();
  const meetingBookingUrl = getMeetingBookingUrl();
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const partnerTheme = usePartnerTheme();
  const partnerLogo = partnerTheme?.logo;
  // null until mounted: the server cannot know which media is on screen.
  const showsMobileMedia = useMatchMedia(MOBILE_MEDIA_QUERY);

  // A widget C2A (Download App Buttons, Calendly, chat) renders its own
  // control rather than a navigational signup/custom button. In that case the
  // hero shows only the widget — the secondary resume-quote / book-a-call
  // buttons are dropped. Widget types often carry a destination anyway (the
  // page they sit on), so the type decides, not the destination.
  const hasWidgetC2a = !!c2a && (!c2a.destination || isWidgetC2aType(c2a.type));

  // A badge with no asset behind it is an unfinished entry, not a gap to draw.
  // Narrowing to a non-null `url` here keeps the JSX free of assertions.
  const heroBadges = badges.flatMap((badge) =>
    badge?.url ? [{ ...badge, url: badge.url }] : [],
  );

  const onC2APress = () => {
    logEvent("USER_HOMEPAGE_GET_QUOTE");
    if (c2a?.destination?.__typename === "Url") {
      router.push(c2a.destination.url!);
    } else if (c2a?.destination?.__typename === "Page") {
      router.push(c2a.destination.slug ?? "");
    }
  };

  return (
    <>
      <div className={styles.container}>
        {/* Desktop (> 1200px) uses the full media; tablet and mobile (<= 1200px)
           use the thumbnail. Breakpoint is the `desktop` token, not `tablet`,
           so tablet-sized viewports get the mobile media. */}
        <div className={classnames(styles.mediaContainer, styles.mediaDesktop)}>
          {media && (
            <HeroAsset
              asset={media}
              poster={desktopPosterImage}
              isOnScreen={showsMobileMedia === false}
              hiddenAt={MOBILE_MEDIA_QUERY}
              fallbackWidth={1920}
              fallbackHeight={1080}
            />
          )}
        </div>
        <div className={classnames(styles.mediaContainer, styles.mediaMobile)}>
          {thumbnail && (
            <HeroAsset
              asset={thumbnail}
              poster={mobilePosterImage}
              isOnScreen={showsMobileMedia === true}
              hiddenAt={DESKTOP_MEDIA_QUERY}
              fallbackWidth={1200}
              fallbackHeight={1200}
            />
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.textContent}>
            {partnerLogo && (
              <div className={styles.partnerLockup}>
                <Image
                  src="/images/logo-with-name-white.svg"
                  alt="Lassie"
                  width={100}
                  height={24}
                  priority
                />
                <span className={styles.partnerDivider} aria-hidden="true" />
                <Image
                  src={partnerLogo.src}
                  alt={partnerLogo.alt}
                  width={partnerLogo.width}
                  height={partnerLogo.height}
                  priority
                />
              </div>
            )}
            <h1 className={styles.title}>{title}</h1>
            {text && (
              <div className={styles.description}>
                <Markdown
                  allowedElements={DESCRIPTION_ELEMENTS}
                  unwrapDisallowed
                  components={descriptionComponents}
                  urlTransform={markdownUrlTransform}
                >
                  {text}
                </Markdown>
              </div>
            )}
            {!hideButtons && (
              <>
                <Gutter size={3} />
                <div
                  className={classnames(styles.ctaGroup, {
                    [styles.ctaGroupWidget]: hasWidgetC2a,
                  })}
                  ref={heroCtaRef}
                >
                  {c2a &&
                    (hasWidgetC2a ? (
                      // Non-navigational C2As (e.g. Download App Buttons, Support,
                      // Chat, Calendly) — defer to the canonical renderer so they
                      // show their proper widget instead of a dead button.
                      <ContentfulC2A {...c2a} stacked />
                    ) : (
                      // Navigational C2As (Standard Button → Url/Page) keep the
                      // hero's bespoke secondary button styling.
                      <ButtonV2
                        variant="secondary"
                        title={c2a.title!}
                        onPress={onC2APress}
                      />
                    ))}
                  {!hasWidgetC2a && signupState?.previewId && (
                    <ButtonV2
                      variant="blurred"
                      title={t("home.sections.0.btn_resume_quote", {
                        name: signupState?.name ?? "?",
                        emoji: "",
                      }).trim()}
                      onPress={() => {
                        logEvent("USER_HOMEPAGE_RESUME_QUOTE");
                        const page = signupState.isLiability
                          ? "liability-quote-preview"
                          : "quote-preview";
                        router.push(
                          `/signup/${page}?id=${signupState?.previewId}`
                        );
                      }}
                    />
                  )}
                  {!hasWidgetC2a && !signupState?.previewId && meetingBookingUrl && (
                    <CalendlyPopUp
                      meetingBookingUrl={meetingBookingUrl}
                      buttonTitle={t("global.call_me")}
                      onClickEvent="USER_HOMEPAGE_CALL_ME"
                      blurredBackground
                    />
                  )}
                </div>
              </>
            )}
            {heroBadges.length > 0 && (
              <>
                <Gutter size={3} />
                <div className={styles.badges}>
                  {heroBadges.map((badge) => (
                    <Image
                      key={badge.url}
                      className={styles.badge}
                      src={badge.url}
                      alt={badge.title ?? badge.description ?? ""}
                      width={badge.width ?? 200}
                      height={badge.height ?? 90}
                    />
                  ))}
                </div>
              </>
            )}
            {disclaimer ? (
              <>
                <Gutter size={2} />
                <div className={styles.disclaimer}>{disclaimer}</div>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {c2a?.title && !hideButtons && !hasWidgetC2a && (
        <FloatingCTA
          heroCtaRef={heroCtaRef}
          title={c2a.title}
          onPress={onC2APress}
        />
      )}
    </>
  );
}
